import { useEffect, useState } from 'react';
import { Heading, Dialog, DialogTrigger, Modal, ModalOverlay } from 'react-aria-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useAtom } from 'jotai';

import { updateLineAttendanceRecordHealth } from '@/api/updateLineAttendanceRecordHealth';
import Button from '@/components/Button/Button';
import {
  AllergyWarningIcon,
  CertificateExpiringIcon,
  CertificateExpiredIcon,
} from '@/components/Icons';
import { LineUserAtom } from '@/jotai/jotai';
import { getGuardianRelationshipLabel } from '@/logics/guardian/guardian';
import { formatTimeToHHMM } from '@/logics/time/timeFormat';
import { AttendanceRecord, UpdateHealthRequest } from '@/types/attendanceRecord';

export const StaffAttendanceRecordDetailPage = () => {
  const [lineUser, _] = useAtom(LineUserAtom);
  const [attendanceRecord, setAttendanceRecord] = useState<AttendanceRecord | null>(null);
  const [isHealthModalOpen, setIsHealthModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [bodyTemperature, setBodyTemperature] = useState<string>('');
  const [condition, setCondition] = useState<string>('');
  const { office_id, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.record) {
      const record = location.state.record as AttendanceRecord;
      setAttendanceRecord(record);
      setBodyTemperature(record.body_temperature?.toString() ?? '');
      setCondition(record.condition ?? '');
    }
  }, [location.state]);

  const handleHealthUpdate = async () => {
    if (!attendanceRecord) return;

    try {
      const updateData: UpdateHealthRequest = {
        body_temperature: bodyTemperature ? parseFloat(bodyTemperature) : undefined,
        condition: condition || undefined,
      };

      const result = await updateLineAttendanceRecordHealth(
        lineUser?.accessToken ?? '',
        attendanceRecord.reservation_id,
        updateData
      );

      if (result.status === 200) {
        setIsHealthModalOpen(false);
      } else {
        alert('バイタル情報の更新に失敗しました');
      }
    } catch (error) {
      console.error('バイタル情報の更新でエラーが発生しました:', error);
      alert('バイタル情報の更新でエラーが発生しました');
    }
  };

  const getAttendanceStatusText = (status: string) => {
    switch (status) {
      case 'attend':
        return '出席';
      case 'absent':
        return '欠席';
      default:
        return status;
    }
  };

  if (!attendanceRecord) {
    return (
      <div className="m-4">
        <div className="py-8 text-center text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="m-4 space-y-4 pb-32">
      <div className="space-y-6">
        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">
            No{' '}
            {location.state?.index !== undefined
              ? location.state.index + 1
              : parseInt(id || '0') + 1}
          </div>
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">管理番号</div>
          <div className="text-lg">{attendanceRecord.child_id}</div>
          <hr className="mt-4 border-gray-200" />
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">学年</div>
          <div className="text-lg">
            {attendanceRecord.school_name}
            {attendanceRecord.school_grade}
          </div>
          <hr className="mt-4 border-gray-200" />
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">利用者（児童）</div>
          <div className="flex items-center">
            <div className="text-lg">{attendanceRecord.child_name}</div>
            <div className="ml-2 flex items-center space-x-1">
              {attendanceRecord.has_allergy && (
                <span className="text-red-500">
                  <AllergyWarningIcon
                    width={20}
                    height={20}
                  />
                </span>
              )}
              {attendanceRecord.certificate_status === 'expiring_soon' && (
                <span className="text-orange-500">
                  <CertificateExpiringIcon
                    width={20}
                    height={20}
                  />
                </span>
              )}
              {attendanceRecord.certificate_status === 'expired' && (
                <span className="text-red-600">
                  <CertificateExpiredIcon
                    width={20}
                    height={20}
                  />
                </span>
              )}
            </div>
          </div>
          <hr className="mt-4 border-gray-200" />
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">出欠</div>
          <Button
            className="mt-2 w-full"
            variant={`${attendanceRecord.attendance_status === 'attend' ? 'solid-fill' : 'outline'}`}
            size="lg"
            type="button"
            disabled
          >
            {getAttendanceStatusText(attendanceRecord.attendance_status)}
          </Button>
          <hr className="mt-4 border-gray-200" />
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">バイタル</div>
          <Button
            className="mt-2 w-full"
            variant="solid-fill"
            size="lg"
            type="button"
            onClick={() => setIsHealthModalOpen(true)}
          >
            入力
          </Button>
          <hr className="mt-4 border-gray-200" />
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">送迎（来所）乗車</div>
          <div className="text-lg">{formatTimeToHHMM(attendanceRecord.pickup_boarding_time)}</div>
          <hr className="mt-4 border-gray-200" />
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-600">送迎（退所）下車</div>
          <div className="text-lg">{formatTimeToHHMM(attendanceRecord.dropoff_get_off_time)}</div>
          <hr className="mt-4 border-gray-200" />
        </div>

        <div>
          <Button
            className="w-full"
            variant="outline"
            size="lg"
            type="button"
            onClick={() => setIsContactModalOpen(true)}
          >
            連絡先
          </Button>
        </div>
      </div>

      <div className="bottom-4 left-4 right-4 space-y-2">
        <Button
          className="w-full"
          variant="solid-fill"
          size="lg"
          type="button"
          onClick={() => navigate(`/staff/offices/${office_id}/transport`)}
        >
          送迎予約一覧
        </Button>
        <Button
          className="w-full"
          variant="solid-fill"
          size="lg"
          type="button"
          onClick={() => navigate(`/staff/offices/${office_id}/attendance-records`)}
        >
          本日の利用者
        </Button>
      </div>

      {/* バイタル入力モーダル */}
      <DialogTrigger
        isOpen={isHealthModalOpen}
        onOpenChange={setIsHealthModalOpen}
      >
        <ModalOverlay>
          <Modal className="fixed inset-4 z-50 rounded-lg bg-white p-6 shadow-lg">
            <Dialog>
              <Heading className="mb-4 text-xl font-bold">バイタル入力</Heading>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">体温（℃）</label>
                  <input
                    type="number"
                    step="0.1"
                    min="30"
                    max="45"
                    value={bodyTemperature}
                    onChange={(e) => setBodyTemperature(e.target.value)}
                    className="w-full rounded-md border border-gray-300 p-2"
                    placeholder="36.5"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">体調メモ</label>
                  <textarea
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    maxLength={500}
                    rows={4}
                    className="w-full rounded-md border border-gray-300 p-2"
                    placeholder="体調について入力してください"
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button
                    className="flex-1 rounded-md bg-gray-500 px-4 py-2 text-white"
                    variant="solid-fill"
                    size="lg"
                    type="button"
                    onClick={() => setIsHealthModalOpen(false)}
                  >
                    キャンセル
                  </Button>
                  <Button
                    className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-white"
                    variant="solid-fill"
                    size="lg"
                    type="button"
                    onClick={handleHealthUpdate}
                  >
                    保存
                  </Button>
                </div>
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </DialogTrigger>

      {/* 連絡先モーダル */}
      <DialogTrigger
        isOpen={isContactModalOpen}
        onOpenChange={setIsContactModalOpen}
      >
        <ModalOverlay>
          <Modal className="fixed inset-4 z-50 rounded-lg bg-white p-6 shadow-lg">
            <Dialog>
              <Heading className="mb-4 text-xl font-bold">連絡先</Heading>
              <div className="space-y-4">
                {attendanceRecord.guardians.map((guardian) => (
                  <div
                    key={guardian.id}
                    className="border-b pb-4 last:border-b-0"
                  >
                    <div className="font-bold">
                      {guardian.relation_ship &&
                        getGuardianRelationshipLabel(guardian.relation_ship)}
                      ：{guardian.last_name} {guardian.first_name}
                    </div>
                    <div className="mt-1">
                      {guardian.phone_number && <div>{guardian.phone_number}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-4">
                <Button
                  className="w-full rounded-md bg-gray-500 px-4 py-2 text-white"
                  variant="solid-fill"
                  size="lg"
                  type="button"
                  onClick={() => setIsContactModalOpen(false)}
                >
                  閉じる
                </Button>
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </DialogTrigger>
    </div>
  );
};
